import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import type { ShouldRevalidateFunction } from 'react-router';
import { z } from 'zod';
import type {
    GetHdeTeamMembersByEmailQuery,
    GetHdeTeamMembersByEmailQueryVariables,
} from 'generated/graphql';
import { GetHdeTeamMembersByEmailDocument } from 'generated/graphql';

export const HDE_CLIENT_DIRECTOR_FALLBACK_API_ROUTE =
    '/remix-api/hde/client-director-fallback';

const QUERY_SCHEMA = z.object({
    emails: z
        .string()
        .min(1)
        .transform((s) =>
            s
                .split(',')
                .map((e) => e.trim().toLowerCase())
                .filter(Boolean),
        ),
    urlSegment: z.string().min(1).default('archipro'),
});

/**
 * Fetches ArchiPro team members by email for the HDE exhibitor fallback.
 * Accepts comma-separated emails and returns matching team members.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);
    const result = QUERY_SCHEMA.safeParse({
        emails: url.searchParams.get('emails'),
        urlSegment: url.searchParams.get('urlSegment') ?? 'archipro',
    });

    if (!result.success) {
        logger.error(
            'Invalid query parameters for HDE client director fallback',
            {
                error: result.error,
            },
        );
        return routerData(null, { status: 400 });
    }

    const { emails, urlSegment } = result.data;
    const seenIds = new Set<number>();
    const teamMembers: NonNullable<
        GetHdeTeamMembersByEmailQuery['professional']
    >['PaginatedTeamMembers']['edges'][number]['node'][] = [];

    try {
        for (const email of emails) {
            const { data } = await context.client.query<
                GetHdeTeamMembersByEmailQuery,
                GetHdeTeamMembersByEmailQueryVariables
            >({
                query: GetHdeTeamMembersByEmailDocument,
                variables: {
                    urlSegment,
                    emailFilter: email,
                    limit: 5,
                },
                fetchPolicy: 'network-only',
            });

            const nodes =
                data?.professional?.PaginatedTeamMembers?.edges
                    ?.map((e) => e.node)
                    .filter(Boolean) ?? [];

            for (const node of nodes) {
                const memberEmail = (node.Email ?? '').trim().toLowerCase();
                if (memberEmail === email && node.ID && !seenIds.has(node.ID)) {
                    seenIds.add(node.ID);
                    teamMembers.push(node);
                }
            }
        }

        return { teamMembers };
    } catch (error) {
        logger.error(`Error in ${HDE_CLIENT_DIRECTOR_FALLBACK_API_ROUTE}`, {
            error,
        });
        return routerData(null, { status: 500 });
    }
};

export const shouldRevalidate: ShouldRevalidateFunction = () => {
    return false;
};
